import {
  AS400_CDB_COLUMN,
  AS400_SUPPLIER_SECTIONS,
  type As400SupplierSection,
} from '@erp/rpc';
import {
  allowedValuesRule,
  asWarning,
  changeFlagFill,
  changeFlagRule,
  notInUseRule,
  onlyIn,
  upcRule,
  type CellAutoFill,
  type CellRow,
  type CellRule,
  type WorksheetRules,
} from '../as400CellRuleKinds';
import { purchasingFactorMustBeOneProblem } from '../stockUomRules';
import { supplierPartOverflowRule } from './supplierPartOverflow';
import {
  warningCodeAllowlistRules,
  warningCodePlacementRules,
  warningCodeRemarkRules,
} from './warningCodeRules';
import { WARNING_CODE_FIELDS } from './warningCodes';

/**
 * The Purchase Data worksheet's own rules, on top of its columns' character
 * limits and number formats (MIN QTY is whole numbers, GEN-2725; the
 * dimensions are 5.4, the UOM factors 4.5 and the minimum amounts 7.2,
 * GEN-2726; a quote number is at most 20, GEN-2771 — all declared on the
 * contract columns). Append new entries; keep one ticket's entries together
 * under its comment.
 *
 * The three supplier sections repeat the same columns numbered 1–3
 * (AS400_SUPPLIER_SECTIONS), so their rules are written once, per section.
 */

// A numeric supplier column holds 0 where nothing was loaded: a 0 there is
// no more "data" than a blank.
const ZERO_MEANS_BLANK = /^(ISUFC|ISMQT|ISMAM)\d$/;
const ZERO = /^0*\.?0*$/;

/** Whether `value` is data, as far as a supplier section is concerned. */
function holdsData(field: string, value: string | null): boolean {
  if (value === null) return false;
  return !(ZERO_MEANS_BLANK.test(field) && ZERO.test(value));
}

/** Whether any of the section's data columns but `except` holds data. */
function sectionHasData(
  section: As400SupplierSection,
  row: CellRow,
  except?: string,
): boolean {
  return section.dataCodes.some(
    (field) => field !== except && holdsData(field, row.value(field)),
  );
}

/** A section's numbered column: `field('ISMQT', section)` is `ISMQT2`. */
function field(base: string, section: As400SupplierSection): string {
  return `${base}${section.number}`;
}

/** Whether `value` is a number that is zero (`0`, `0.000`). */
function isZero(value: string | null): boolean {
  return value !== null && /\d/.test(value) && ZERO.test(value);
}

// GEN-2764: the AS/400 rejects a supplier section whose Ship Direct flag isn't
// Y, and deletes everything entered to the left of it.
function shipDirectRule(section: As400SupplierSection): CellRule {
  return {
    field: section.shipDirectCode,
    dependsOn: section.dataCodes,
    check(value, row) {
      if (value === 'Y' || !sectionHasData(section, row)) return null;
      return { rule: 'ship-direct', section: section.number };
    },
  };
}

function shipDirectFill(section: As400SupplierSection): CellAutoFill {
  return {
    when: section.dataCodes,
    fill: (row) =>
      sectionHasData(section, row) ? { [section.shipDirectCode]: 'Y' } : null,
  };
}

// GEN-2770: a new item's supplier needs its minimum order quantity. A 0 is no
// more a quantity than a blank is (ZERO_MEANS_BLANK).
function minQuantityRequiredRule(section: As400SupplierSection): CellRule {
  const minQuantity = field('ISMQT', section);
  return onlyIn('add', {
    field: minQuantity,
    dependsOn: section.dataCodes.filter((code) => code !== minQuantity),
    check(value, row) {
      if (
        holdsData(minQuantity, value) ||
        !sectionHasData(section, row, minQuantity)
      ) {
        return null;
      }
      return { rule: 'min-quantity-required', section: section.number };
    },
  });
}

function purchasingFactorRequiredRule(section: As400SupplierSection): CellRule {
  const factor = field('ISUFC', section);
  return onlyIn('add', {
    field: factor,
    dependsOn: section.dataCodes.filter((code) => code !== factor),
    check(value, row) {
      if (holdsData(factor, value) || !sectionHasData(section, row, factor)) {
        return null;
      }
      return { rule: 'purchasing-factor-required', section: section.number };
    },
  });
}

function purchasingFactorMustBeOneRule(
  section: As400SupplierSection,
): CellRule {
  const factor = field('ISUFC', section);
  const uom = field('ISUOM', section);
  return {
    field: factor,
    dependsOn: [uom, 'ITEMNO', AS400_CDB_COLUMN],
    check(value, row) {
      const itemNumber = row.value('ITEMNO');
      if (!row.stockUoms || !itemNumber) return null;
      return purchasingFactorMustBeOneProblem({
        factor: value,
        purchaseUom: row.value(uom),
        stockUom: row.stockUoms(itemNumber, row.value(AS400_CDB_COLUMN)),
      });
    },
  };
}

// GEN-2770: the minimum dollar amount has gone unused for about 19 years;
// anything but 0 risks the upload. Warned, not blocked.
function minAmountUnusedRule(section: As400SupplierSection): CellRule {
  return asWarning({
    field: field('ISMAM', section),
    check: (value) =>
      value === null || isZero(value) ? null : { rule: 'should-be-zero' },
  });
}

// GEN-2771: a quote can't load without the supplier it is from.
function quoteNeedsSupplierRule(section: As400SupplierSection): CellRule {
  const supplier = field('ISUPL', section);
  return {
    field: field('ISQTE', section),
    dependsOn: [supplier],
    check(value, row) {
      if (value === null || row.value(supplier) !== null) return null;
      return { rule: 'needs-field', field: supplier };
    },
  };
}

function supplierSectionRules(section: As400SupplierSection): CellRule[] {
  const quote = field('ISQTE', section);
  return [
    shipDirectRule(section),
    minQuantityRequiredRule(section),
    purchasingFactorRequiredRule(section),
    purchasingFactorMustBeOneRule(section),
    onlyIn(
      'change',
      changeFlagRule(field('CHG_ISMQT', section), field('ISMQT', section)),
    ),
    minAmountUnusedRule(section),
    // GEN-2765: CA or US only; a European vendor is entered as US.
    allowedValuesRule(field('ISCTR', section), ['CA', 'US'], {
      blankAllowed: true,
    }),
    asWarning(upcRule(field('IITM', section), field('IMID', section))),
    quoteNeedsSupplierRule(section),
    // GEN-2771: loading or clearing a quote takes its Y flag, adds included.
    changeFlagRule(`UPDATE_${quote}`, quote),
  ];
}

function supplierSectionFills(section: As400SupplierSection): CellAutoFill[] {
  const quote = field('ISQTE', section);
  return [
    shipDirectFill(section),
    {
      ...changeFlagFill(field('CHG_ISMQT', section), field('ISMQT', section)),
      modes: ['change'],
    },
    changeFlagFill(`UPDATE_${quote}`, quote),
  ];
}

// GEN-2775: height, width, length, weight and density, at the each, package
// and pallet levels — each with its own UPDATE flag.
const DIMENSIONS = ['HIG', 'WID', 'LEN', 'WHT', 'DNS'].flatMap((measure) =>
  ['EA', 'PG', 'PT'].map((level) => `I${measure}${level}`),
);

// A 0 fails the upload — warned, not blocked, until Rexel's test says whether
// a 0 without its flag is ignored. Clearing one is a blank plus UPDATE Y,
// never a 0 or slashes; the flag is filled on any change of an existing item.
function dimensionRules(dimension: string): CellRule[] {
  return [
    asWarning({
      field: dimension,
      check: (value) => (isZero(value) ? { rule: 'zero-dimension' } : null),
    }),
    onlyIn(
      'change',
      changeFlagRule(`UPDATE_${dimension}`, dimension, { clearsOnly: true }),
    ),
  ];
}

// GEN-2883: the warning codes and the product remarks each load only with
// their own UPDATE Y/N, filled on any change of an existing item.
const UPDATE_FLAGGED = [
  ...WARNING_CODE_FIELDS,
  'IOTH1E',
  'IOTH2E',
  'IOTH1F',
  'IOTH2F',
];

// GEN-2765: Supplier 3 normally mirrors Supplier 1, so a new item's Supplier 3
// starts as a copy of whatever Supplier 1 has that it lacks. One pass: the
// copy sets Supplier 3's Ship Direct flag itself, as no fill sets off another.
const MIRRORED = ['ISUPL', 'ISCTR', 'ISPRD', 'ISUOM', 'ISUFC'];
const SUPPLIER_3_FROM_1: CellAutoFill = {
  when: MIRRORED.map((base) => `${base}1`),
  modes: ['add'],
  fill(row) {
    const copies: Record<string, string> = {};
    for (const base of MIRRORED) {
      const from = row.value(`${base}1`);
      if (from !== null && row.value(`${base}3`) === null) {
        copies[`${base}3`] = from;
      }
    }
    if (Object.keys(copies).length === 0) return null;
    return { ...copies, ISHD3: 'Y' };
  },
};

export const PURCHASE_SHEET_RULES: WorksheetRules = {
  rules: [
    // GEN-2764, GEN-2770, GEN-2771, GEN-2765: per supplier section.
    ...AS400_SUPPLIER_SECTIONS.flatMap(supplierSectionRules),
    // GEN-2775
    ...DIMENSIONS.flatMap(dimensionRules),
    // GEN-2766: a Supplier 1 part number over 25 characters goes in the
    // description instead.
    supplierPartOverflowRule,
    asWarning(notInUseRule('ISTATH', 'Vera-Lynne')),
    ...AS400_SUPPLIER_SECTIONS.flatMap((section) => [
      asWarning(notInUseRule(field('IPAK', section), 'Vera-Lynne')),
      asWarning(notInUseRule(field('IMID', section), 'Vera-Lynne')),
    ]),
    asWarning(notInUseRule('IDNSEA', 'Vera-Lynne')),
    asWarning(notInUseRule('IDNSPG', 'Vera-Lynne')),
    asWarning(notInUseRule('IDNSPT', 'Vera-Lynne')),
    ...warningCodeAllowlistRules(),
    ...warningCodePlacementRules(),
    // GEN-2883
    ...warningCodeRemarkRules(),
    ...UPDATE_FLAGGED.map((target) =>
      onlyIn('change', changeFlagRule(`UPDATE_${target}`, target)),
    ),
  ],
  autoFills: [
    // GEN-2764, GEN-2770, GEN-2771: per supplier section.
    ...AS400_SUPPLIER_SECTIONS.flatMap(supplierSectionFills),
    // GEN-2775
    ...DIMENSIONS.map((dimension) => ({
      ...changeFlagFill(`UPDATE_${dimension}`, dimension),
      modes: ['change' as const],
    })),
    // GEN-2765
    SUPPLIER_3_FROM_1,
    // GEN-2883
    ...UPDATE_FLAGGED.map((target) => ({
      ...changeFlagFill(`UPDATE_${target}`, target),
      modes: ['change' as const],
    })),
  ],
  addDefaults: [],
};
